import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ringtone using Web Audio API (no external file needed)
const useRingtone = () => {
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);
  const intervalRef = useRef(null);

  const startRingtone = () => {
    try {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      
      const playTone = () => {
        if (!audioContextRef.current) return;
        
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        // Classic phone ring frequencies
        oscillator.frequency.setValueAtTime(440, audioContextRef.current.currentTime); // A4
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.5);
        
        oscillator.start(audioContextRef.current.currentTime);
        oscillator.stop(audioContextRef.current.currentTime + 0.5);
        
        // Second tone (higher pitch)
        setTimeout(() => {
          if (!audioContextRef.current) return;
          const osc2 = audioContextRef.current.createOscillator();
          const gain2 = audioContextRef.current.createGain();
          
          osc2.connect(gain2);
          gain2.connect(audioContextRef.current.destination);
          
          osc2.frequency.setValueAtTime(520, audioContextRef.current.currentTime);
          osc2.type = 'sine';
          
          gain2.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.5);
          
          osc2.start(audioContextRef.current.currentTime);
          osc2.stop(audioContextRef.current.currentTime + 0.5);
        }, 200);
      };
      
      // Play immediately then repeat
      playTone();
      intervalRef.current = setInterval(playTone, 2000);
      
    } catch (e) {
      console.warn('[Ringtone] Could not play ringtone:', e);
    }
  };

  const stopRingtone = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  return { startRingtone, stopRingtone };
};

function IncomingCallModal({ call, onAccept, onReject }) {
  const { startRingtone, stopRingtone } = useRingtone();
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes timeout

  useEffect(() => {
    // Start ringtone when modal opens
    startRingtone();
    
    // Countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Auto-reject after timeout
          onReject();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      stopRingtone();
      clearInterval(timer);
    };
  }, []);

  const handleAccept = () => {
    stopRingtone();
    onAccept();
  };

  const handleReject = () => {
    stopRingtone();
    onReject();
  };

  return (
    <div className="incoming-call-overlay" data-testid="incoming-call-modal">
      <div className="incoming-call-modal">
        <div className="call-animation">
          <div className="pulse-ring"></div>
          <div className="pulse-ring delay-1"></div>
          <div className="pulse-ring delay-2"></div>
          <div className="caller-avatar">
            <User size={48} />
          </div>
        </div>
        
        <h2>Appel entrant</h2>
        <p className="caller-name">{call.caller_name}</p>
        <p className="call-type">Téléconsultation vidéo</p>
        
        <div className="call-timer">
          Expire dans {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
        </div>
        
        <div className="call-actions">
          <button 
            className="btn-reject" 
            onClick={handleReject}
            data-testid="reject-call-btn"
          >
            <PhoneOff size={28} />
            <span>Refuser</span>
          </button>
          <button 
            className="btn-accept" 
            onClick={handleAccept}
            data-testid="accept-call-btn"
          >
            <Phone size={28} />
            <span>Accepter</span>
          </button>
        </div>
      </div>
      
      <style>{`
        .incoming-call-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .incoming-call-modal {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          border-radius: 24px;
          padding: 40px;
          text-align: center;
          max-width: 350px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }
        
        .call-animation {
          position: relative;
          width: 120px;
          height: 120px;
          margin: 0 auto 24px;
        }
        
        .pulse-ring {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: 3px solid #22c55e;
          border-radius: 50%;
          animation: pulse 2s ease-out infinite;
        }
        
        .pulse-ring.delay-1 {
          animation-delay: 0.5s;
        }
        
        .pulse-ring.delay-2 {
          animation-delay: 1s;
        }
        
        @keyframes pulse {
          0% {
            transform: scale(0.8);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        
        .caller-avatar {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        
        .incoming-call-modal h2 {
          color: #22c55e;
          font-size: 1.5rem;
          margin: 0 0 8px 0;
        }
        
        .caller-name {
          color: white;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0 0 4px 0;
        }
        
        .call-type {
          color: #94a3b8;
          font-size: 0.9rem;
          margin: 0 0 16px 0;
        }
        
        .call-timer {
          color: #f59e0b;
          font-size: 0.85rem;
          margin-bottom: 24px;
        }
        
        .call-actions {
          display: flex;
          gap: 24px;
          justify-content: center;
        }
        
        .call-actions button {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px 24px;
          border: none;
          border-radius: 16px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          font-size: 0.9rem;
          font-weight: 500;
        }
        
        .call-actions button:hover {
          transform: scale(1.05);
        }
        
        .call-actions button:active {
          transform: scale(0.95);
        }
        
        .btn-accept {
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          box-shadow: 0 4px 20px rgba(34, 197, 94, 0.4);
        }
        
        .btn-accept:hover {
          box-shadow: 0 6px 30px rgba(34, 197, 94, 0.6);
        }
        
        .btn-reject {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          box-shadow: 0 4px 20px rgba(239, 68, 68, 0.4);
        }
        
        .btn-reject:hover {
          box-shadow: 0 6px 30px rgba(239, 68, 68, 0.6);
        }
      `}</style>
    </div>
  );
}

export default IncomingCallModal;
